import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Send as SendIcon,
  Close as CloseIcon,
  CheckCircleOutline as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

type QuestionType =
  | 'text'
  | 'multiple_choice'
  | 'yes_no'
  | 'yes_no_na'
  | 'number'
  | 'date'
  | 'signature'
  | 'text_header'
  | 'email_check'
  | 'image_type';

export interface AIGeneratedQuestion {
  question_text: string;
  question_type: QuestionType;
  is_required: boolean;
  image_required?: boolean;
  force_image_on_yes?: boolean;
  force_image_on_no?: boolean;
  options?: string[];
  allow_multiple_selections?: boolean;
  min_selections?: number;
  max_selections?: number;
}

export interface AIGeneratedTemplate {
  template_name: string;
  description: string;
  suggested_tags: string[];
  questions: AIGeneratedQuestion[];
}

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface AITemplateGeneratorDialogProps {
  open: boolean;
  onClose: () => void;
  initialTemplateName?: string;
  filterTag?: string | null;
  onApplyTemplate: (template: AIGeneratedTemplate) => void;
}

const QUESTION_TYPE_LABELS: Record<string, string> = {
  yes_no: 'Yes / No',
  yes_no_na: 'Yes / No / N/A',
  text: 'Text',
  multiple_choice: 'Multiple Choice',
  number: 'Number',
  date: 'Date',
  signature: 'Signature',
  text_header: 'Section Header',
  image_type: 'Photo',
  email_check: 'Email',
};

const AITemplateGeneratorDialog: React.FC<AITemplateGeneratorDialogProps> = ({
  open,
  onClose,
  initialTemplateName = '',
  filterTag,
  onApplyTemplate,
}) => {
  const theme = useTheme();
  const [templateName, setTemplateName] = useState(initialTemplateName);
  const [inputValue, setInputValue] = useState('');
  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
  const [generatedTemplate, setGeneratedTemplate] = useState<AIGeneratedTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<'name_entry' | 'conversation' | 'preview'>('name_entry');
  const [pendingAiQuestions, setPendingAiQuestions] = useState<string[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setTemplateName(initialTemplateName);
      setInputValue('');
      setConversationHistory([]);
      setGeneratedTemplate(null);
      setLoading(false);
      setError(null);
      setStage('name_entry');
      setPendingAiQuestions([]);
    }
  }, [open, initialTemplateName]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory, loading]);

  const callGenerateApi = async (name: string, history: ConversationMessage[]) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/check-templates/generate`,
        {
          template_name: name,
          template_type: filterTag || undefined,
          conversation_history: history,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );

      const result = response.data;

      if (result.action === 'ask_questions') {
        const assistantText =
          result.message +
          '\n\n' +
          (result.questions as string[]).map((q: string, i: number) => `${i + 1}. ${q}`).join('\n');

        const newHistory: ConversationMessage[] = [
          ...history,
          { role: 'assistant', content: assistantText },
        ];
        setConversationHistory(newHistory);
        setPendingAiQuestions(result.questions || []);
        setStage('conversation');
      } else if (result.action === 'generate_template' && result.template) {
        const tpl: AIGeneratedTemplate = result.template;
        const summaryText = `I've generated your **${tpl.template_name}** template with ${tpl.questions.length} questions. Review it below and click **Apply to Builder** when you're happy.`;
        const newHistory: ConversationMessage[] = [
          ...history,
          { role: 'assistant', content: summaryText },
        ];
        setConversationHistory(newHistory);
        setGeneratedTemplate(tpl);
        setPendingAiQuestions([]);
        setStage('preview');
      } else {
        setError('Unexpected response from AI. Please try again.');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to connect to AI service. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!templateName.trim()) return;
    const userMessage: ConversationMessage = {
      role: 'user',
      content: `Create a check template named "${templateName.trim()}"${filterTag ? ` (type: ${filterTag})` : ''}.`,
    };
    setConversationHistory([userMessage]);
    setStage('conversation');
    await callGenerateApi(templateName.trim(), []);
  };

  const handleSendReply = async () => {
    if (!inputValue.trim() || loading) return;
    const reply = inputValue.trim();
    setInputValue('');

    const newHistory: ConversationMessage[] = [
      ...conversationHistory,
      { role: 'user', content: reply },
    ];
    setConversationHistory(newHistory);
    await callGenerateApi(templateName.trim(), newHistory);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (stage === 'name_entry') {
        handleStart();
      } else {
        handleSendReply();
      }
    }
  };

  const handleApply = () => {
    if (generatedTemplate) {
      onApplyTemplate(generatedTemplate);
      onClose();
    }
  };

  const handleRegenerate = async () => {
    setGeneratedTemplate(null);
    setStage('conversation');
    const regenMessage: ConversationMessage = {
      role: 'user',
      content: 'Please regenerate the template with different questions.',
    };
    const newHistory = [...conversationHistory, regenMessage];
    setConversationHistory(newHistory);
    await callGenerateApi(templateName.trim(), newHistory);
  };

  const renderMessage = (msg: ConversationMessage, idx: number) => {
    const isUser = msg.role === 'user';
    return (
      <Box
        key={idx}
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 1.5,
        }}
      >
        {!isUser && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || '#9c27b0'})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1,
              flexShrink: 0,
              mt: 0.5,
            }}
          >
            <AIIcon sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
        )}
        <Paper
          elevation={0}
          sx={{
            maxWidth: '80%',
            px: 2,
            py: 1.5,
            borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            bgcolor: isUser
              ? theme.palette.primary.main
              : alpha(theme.palette.primary.main, 0.07),
            color: isUser ? '#fff' : 'text.primary',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {msg.content}
          </Typography>
        </Paper>
      </Box>
    );
  };

  const renderPreview = () => {
    if (!generatedTemplate) return null;
    return (
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <CheckIcon color="success" />
          <Typography variant="subtitle1" fontWeight={600}>
            Generated Template Preview
          </Typography>
          {generatedTemplate.suggested_tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" color="primary" variant="outlined" />
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {generatedTemplate.description}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {generatedTemplate.questions.length} Questions
        </Typography>
        <List dense sx={{ mt: 0.5, maxHeight: 280, overflow: 'auto', border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
          {generatedTemplate.questions.map((q, i) => (
            <ListItem
              key={i}
              divider={i < generatedTemplate.questions.length - 1}
              sx={{
                py: 0.75,
                bgcolor: q.question_type === 'text_header' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {i + 1}
                </Typography>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    fontWeight={q.question_type === 'text_header' ? 700 : 400}
                  >
                    {q.question_text}
                  </Typography>
                }
                secondary={
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                    <Chip
                      label={QUESTION_TYPE_LABELS[q.question_type] || q.question_type}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    {q.is_required && (
                      <Chip label="Required" size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {q.image_required && (
                      <Chip label="Photo required" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {q.options && q.options.length > 0 && (
                      <Chip
                        label={q.options.join(', ')}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || '#9c27b0'})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AIIcon sx={{ fontSize: 20, color: '#fff' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            AI Template Generator
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Powered by Policy Max
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {stage === 'name_entry' ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the name of the template you want to create and the AI will build it for you — including all questions, types, and conditions.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Template Name"
              placeholder='e.g. "Forklift Pre-Use Inspection" or "Vehicle Delivery Note"'
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={handleKeyDown}
              helperText="Be specific about the equipment type or industry for best results"
            />
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 200 }}>
              {conversationHistory.map((msg, idx) => renderMessage(msg, idx))}
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || '#9c27b0'})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <AIIcon sx={{ fontSize: 18, color: '#fff' }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center', height: 24 }}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: theme.palette.primary.main,
                          animation: 'bounce 1.2s infinite',
                          animationDelay: `${i * 0.2}s`,
                          '@keyframes bounce': {
                            '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                            '40%': { transform: 'scale(1)', opacity: 1 },
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {stage === 'preview' && renderPreview()}

            {stage === 'conversation' && !loading && pendingAiQuestions.length > 0 && (
              <Box
                component="form"
                onSubmit={(e) => { e.preventDefault(); handleSendReply(); }}
                sx={{ display: 'flex', gap: 1, mt: 1 }}
              >
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  maxRows={4}
                  placeholder="Type your answer here..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  autoFocus
                />
                <Tooltip title="Send">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleSendReply}
                      disabled={!inputValue.trim() || loading}
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: '#fff',
                        '&:hover': { bgcolor: theme.palette.primary.dark },
                        '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                      }}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>

        {stage === 'name_entry' && (
          <Button
            variant="contained"
            onClick={handleStart}
            disabled={!templateName.trim() || loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AIIcon />}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || '#9c27b0'})`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary?.dark || '#7b1fa2'})`,
              },
            }}
          >
            {loading ? 'Generating…' : 'Generate Template'}
          </Button>
        )}

        {stage === 'preview' && generatedTemplate && (
          <>
            <Tooltip title="Generate a new version">
              <Button
                variant="outlined"
                onClick={handleRegenerate}
                disabled={loading}
                startIcon={<RefreshIcon />}
              >
                Regenerate
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleApply}
              startIcon={<CheckIcon />}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || '#9c27b0'})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary?.dark || '#7b1fa2'})`,
                },
              }}
            >
              Apply to Builder
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AITemplateGeneratorDialog;
